<template>
  <div class="future-calendar-test">
    <h3 class="global-h3" style="margin-bottom: 15px">Future Calendar Test</h3>
    <p class="global-p" style="margin-bottom: 20px">Test generating a future calendar to see what topics would be created and if more content is needed.</p>

    <!-- Input Form -->
    <div class="test-form">
      <div class="test-form__input-group">
        <label class="test-form__label">Start</label>
        <input v-model="genStartDate" type="date" class="test-form__input" :disabled="isLoading" />
      </div>

      <div class="test-form__input-group">
        <label class="test-form__label">Weeks</label>
        <input v-model.number="weeksToGen" type="number" min="1" max="12" class="test-form__input" :disabled="isLoading" />
      </div>

      <SrpButton @click="runTest" :isDisabled="isLoading" style="margin-top: 10px">
        <template #icon>
          <LoadingIcon v-if="isLoading" :size="20" />
          <IconEmbedded v-else name="ai-triple-solid" :size="26" />
        </template>
        Run Test
      </SrpButton>
    </div>

    <!-- Previous Tests List -->
    <div v-if="summaries.length > 0" class="summary-list">
      <h4 class="global-h4" style="margin-bottom: 15px">Previous Tests</h4>
      <div class="summary-list__items">
        <div v-for="summary in summaries" :key="summary.id" class="summary-list__item" :class="{ 'summary-list__item--selected': selectedSummary?.id === summary.id }" @click="selectSummary(summary)">
          <div class="summary-list__item-header">
            <div class="summary-list__item-left">
              <span class="summary-list__item-date">Starting {{ summary.startDate }} for {{ summary.weeksGenerated }} weeks</span>
              <span class="summary-list__item-source">{{ summary.source }}</span>
            </div>
            <span :class="getStatusBadgeClass(summary.status)">
              <LoadingIcon v-if="summary.isProcessing" :size="12" style="margin-right: 4px" />
              {{ summary.status }}
            </span>
          </div>
          <div class="summary-list__item-details">Kicked off {{ formatDate(summary.createdDate) }}</div>
        </div>
      </div>
    </div>

    <!-- Results Display -->
    <div v-if="selectedSummary && selectedSummary.status === 'Done'" class="test-results">
      <h4 class="global-h4" style="margin-bottom: 15px">Topic Summary</h4>

      <!-- Topic Summary (most prominent) -->
      <div v-if="selectedSummary.topicSummary" class="test-results__topic-summary">
        <div class="test-results__text">{{ selectedSummary.topicSummary }}</div>
      </div>

      <!-- More Content Needed (highlighted) -->
      <div class="test-results__content-needed-section">
        <span class="test-results__section-title">Content Needed: </span>
        <span
          style="margin-left: 8px"
          :class="{
            'test-results__badge': true,
            'test-results__badge--none': selectedSummary.moreContentNeeded?.toLowerCase() === 'none',
            'test-results__badge--low': selectedSummary.moreContentNeeded?.toLowerCase() === 'low',
            'test-results__badge--medium': selectedSummary.moreContentNeeded?.toLowerCase() === 'medium',
            'test-results__badge--high': selectedSummary.moreContentNeeded?.toLowerCase() === 'high',
          }"
        >
          {{ selectedSummary.moreContentNeeded || "N/A" }}
        </span>
      </div>

      <!-- More Content Guidance -->
      <div v-if="selectedSummary.moreContentGuidance" class="test-results__content-guidance">
        <div class="test-results__text">{{ selectedSummary.moreContentGuidance }}</div>
      </div>

      <!-- Statistics Row (less prominent) -->
      <div class="test-results__stats">
        <div class="test-results__stat">
          <span class="test-results__stat-label">Topics Generated:</span>
          <span class="test-results__stat-value">{{ selectedSummary.topicsGenerated }}</span>
        </div>
        <div class="test-results__stat">
          <span class="test-results__stat-label">Topics Matched:</span>
          <span class="test-results__stat-value">{{ selectedSummary.topicsMatched }}</span>
        </div>
        <div class="test-results__stat">
          <span class="test-results__stat-label">Content Summaries:</span>
          <span class="test-results__stat-value">{{ selectedSummary.contentSummariesCount }}</span>
        </div>
        <div class="test-results__stat">
          <span class="test-results__stat-label">Total Photos:</span>
          <span class="test-results__stat-value">{{ selectedSummary.totalPhotoCount }}</span>
        </div>
      </div>
    </div>

    <!-- Processing State -->
    <div v-else-if="selectedSummary && (selectedSummary.status === 'New' || selectedSummary.isProcessing)" class="test-processing">
      <LoadingIcon :size="40" />
      <div class="test-processing__text">Processing... This may take a minute.</div>
    </div>

    <!-- Failed State -->
    <div v-else-if="selectedSummary && selectedSummary.status === 'Failed'" class="test-error">
      <div class="test-error__title">Processing Failed</div>
      <div class="test-error__message">The test failed to complete. Please try again.</div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="test-error">
      <div class="test-error__title">Error</div>
      <div class="test-error__message">{{ error }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import DayjsExtended from "@logic/DayjsExtended";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { CalendarGenResultSummaryData } from "@contracts/contentCalendar";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

const props = defineProps<{
  customerId: string;
}>();

// Form inputs
const genStartDate = ref<string>("");
const weeksToGen = ref<number>(4);

// State
const isLoading = ref<boolean>(false);
const summaries = ref<CalendarGenResultSummaryData[]>([]);
const selectedSummary = ref<CalendarGenResultSummaryData | null>(null);
const error = ref<string | null>(null);
let pollingInterval: number | null = null;

// Initialize with next Monday and load existing summaries
onMounted(async () => {
  const nextMonday = DayjsExtended().day(8).format("YYYY-MM-DD"); // day(8) gets next Monday
  genStartDate.value = nextMonday;

  await loadSummaries();
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});

async function loadSummaries() {
  summaries.value = await ContentCalendarRepo.getSummaries(props.customerId);

  // If we have a selected summary, update it with the latest data
  if (selectedSummary.value) {
    const updated = summaries.value.find(s => s.id === selectedSummary.value?.id);
    if (updated) {
      selectedSummary.value = updated;
    }
  }
}

function startPolling() {
  // Poll every 30 seconds
  pollingInterval = window.setInterval(async () => {
    // Only poll if there are processing summaries
    const hasProcessing = summaries.value.some(s => s.status === "New" || s.isProcessing);
    if (hasProcessing) {
      await loadSummaries();
    }
  }, 30000);
}

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

async function runTest() {
  if (!genStartDate.value) {
    error.value = "Please select a start date";
    return;
  }

  isLoading.value = true;
  error.value = null;

  const newSummary = await ContentCalendarRepo.testFutureCalendarGen(props.customerId, genStartDate.value, weeksToGen.value);
  isLoading.value = false;

  // Add to list and select it
  await loadSummaries();
  selectedSummary.value = newSummary;
}

function selectSummary(summary: CalendarGenResultSummaryData) {
  selectedSummary.value = summary;
}

function getStatusBadgeClass(status: string) {
  return {
    "summary-list__status-badge": true,
    "summary-list__status-badge--new": status === "New",
    "summary-list__status-badge--done": status === "Done",
    "summary-list__status-badge--failed": status === "Failed",
    "summary-list__status-badge--redo": status === "Redo",
  };
}

function formatDate(dateString: string) {
  return DayjsExtended(dateString).format("YYYY-MM-DD h:mm A");
}
</script>

<style scoped lang="scss">
.future-calendar-test {
  padding: 30px;
  border: 2px solid rgba(204, 162, 47, 0.3);
  border-radius: 8px;
  background: rgba(255, 252, 240, 0.5);
  margin-top: 30px;
}

.test-form {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  margin-bottom: 30px;

  &__input-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7);
  }

  &__input {
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    font-size: 14px;
    min-width: 150px;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.test-results {
  &__topic-summary {
    padding: 20px;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    margin-bottom: 20px;
  }

  &__content-needed-section {
    margin-bottom: 20px;
  }

  &__content-guidance {
    padding: 15px;
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    margin-bottom: 20px;
  }

  &__section-title {
    font-size: 19px;
    font-weight: 700;
  }

  &__text {
    font-size: 15px;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.85);
  }

  &__badge {
    display: inline-block;
    padding: 10px 20px;
    border-radius: 20px;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;

    &--none {
      background: rgba(76, 175, 80, 0.2);
      color: rgb(27, 94, 32);
      border: 2px solid rgb(76, 175, 80);
    }

    &--low {
      background: rgba(156, 204, 101, 0.2);
      color: rgb(51, 105, 30);
      border: 2px solid rgb(156, 204, 101);
    }

    &--medium {
      background: rgba(255, 193, 7, 0.2);
      color: rgb(245, 124, 0);
      border: 2px solid rgb(255, 193, 7);
    }

    &--high {
      background: rgba(244, 67, 54, 0.2);
      color: rgb(183, 28, 28);
      border: 2px solid rgb(244, 67, 54);
    }
  }

  &__stats {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    padding: 15px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__stat-label {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  &__stat-value {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }
}

.summary-list {
  margin-bottom: 30px;

  &__items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    padding: 15px;
    background: white;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: rgba(204, 162, 47, 0.5);
      background: rgba(255, 252, 240, 0.3);
    }

    &--selected {
      border-color: rgba(204, 162, 47, 0.8);
      background: rgba(255, 252, 240, 0.6);
    }
  }

  &__item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  &__item-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__item-date {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  &__item-source {
    font-size: 12px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.5);
    padding: 2px 8px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  &__item-details {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
  }

  &__status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--new {
      background: rgba(33, 150, 243, 0.15);
      color: rgb(13, 71, 161);
      border: 1px solid rgba(33, 150, 243, 0.5);
    }

    &--done {
      background: rgba(76, 175, 80, 0.15);
      color: rgb(27, 94, 32);
      border: 1px solid rgba(76, 175, 80, 0.5);
    }

    &--failed {
      background: rgba(244, 67, 54, 0.15);
      color: rgb(183, 28, 28);
      border: 1px solid rgba(244, 67, 54, 0.5);
    }

    &--redo {
      background: rgba(255, 152, 0, 0.15);
      color: rgb(230, 81, 0);
      border: 1px solid rgba(255, 152, 0, 0.5);
    }
  }
}

.test-processing {
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;

  &__text {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.7);
    font-weight: 500;
  }
}

.test-error {
  padding: 15px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 6px;

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: rgb(183, 28, 28);
    margin-bottom: 5px;
  }

  &__message {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
  }
}
</style>
